import React, { Component } from "react";
import "./AgentCore.css";

/**
 * A small, dependency-free Three.js scene used as the portfolio's visual
 * signature. Three is loaded from the existing site CDN so this component
 * deliberately relies on window.THREE rather than adding a second renderer.
 */
class AgentCore extends Component {
  constructor(props) {
    super(props);
    this.mountRef = React.createRef();
    this.frameId = null;
    this.isVisible = true;
    this.pointer = { x: 0, y: 0 };
    this.reducedMotion = false;
  }

  componentDidMount() {
    this.createScene();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isDark !== this.props.isDark) {
      this.destroyScene();
      this.createScene();
    }
  }

  componentWillUnmount() {
    this.destroyScene();
  }

  createScene = () => {
    const mount = this.mountRef.current;
    const THREE = window.THREE;

    if (!mount || !THREE || !THREE.WebGLRenderer) return;

    try {
      this.reducedMotion = window.matchMedia
        ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
        : false;

      const width = Math.max(mount.clientWidth, 1);
      const height = Math.max(mount.clientHeight, 1);
      const isDark = this.props.isDark !== false;
      const palette = isDark
        ? {
            core: 0x7cf7ff,
            electric: 0x8678ff,
            warm: 0xffa56f,
            particle: 0x9ec8ff,
            ghost: 0x203d6b,
          }
        : {
            core: 0x087b89,
            electric: 0x5b52cf,
            warm: 0xcd6641,
            particle: 0x156176,
            ghost: 0x9ddce0,
          };

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(42, width / height, 0.1, 100);
      camera.position.set(0, 0.08, 6.2);

      const renderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: true,
        powerPreference: "high-performance",
      });
      renderer.setClearColor(0x000000, 0);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
      renderer.setSize(width, height);
      renderer.domElement.setAttribute("aria-hidden", "true");
      mount.appendChild(renderer.domElement);

      const coreGroup = new THREE.Group();
      coreGroup.rotation.set(-0.25, 0.18, 0);
      scene.add(coreGroup);

      const outerMaterial = new THREE.MeshBasicMaterial({
        color: palette.electric,
        transparent: true,
        opacity: 0.22,
        wireframe: true,
      });
      const outerCore = new THREE.Mesh(
        new THREE.IcosahedronGeometry(1.55, 2),
        outerMaterial
      );
      coreGroup.add(outerCore);

      const haloCore = new THREE.Mesh(
        new THREE.IcosahedronGeometry(1.12, 2),
        new THREE.MeshBasicMaterial({
          color: palette.core,
          transparent: true,
          opacity: 0.14,
          wireframe: true,
        })
      );
      coreGroup.add(haloCore);

      const coreMaterial = new THREE.MeshStandardMaterial({
        color: palette.core,
        emissive: palette.core,
        emissiveIntensity: isDark ? 1.15 : 0.48,
        roughness: 0.22,
        metalness: 0.7,
        flatShading: true,
        transparent: true,
        opacity: 0.9,
      });
      const core = new THREE.Mesh(
        new THREE.IcosahedronGeometry(0.73, 2),
        coreMaterial
      );
      coreGroup.add(core);

      const innerWire = new THREE.Mesh(
        new THREE.IcosahedronGeometry(0.88, 1),
        new THREE.MeshBasicMaterial({
          color: palette.warm,
          transparent: true,
          opacity: 0.42,
          wireframe: true,
        })
      );
      coreGroup.add(innerWire);

      const ringConfigs = [
        {
          radius: 1.9,
          rotation: [1.34, 0.1, 0.55],
          color: palette.core,
          speed: 0.0032,
        },
        {
          radius: 2.25,
          rotation: [0.52, 0.56, -0.7],
          color: palette.electric,
          speed: -0.0024,
        },
        {
          radius: 2.58,
          rotation: [2.12, 0.1, 0.15],
          color: palette.warm,
          speed: 0.0017,
        },
      ];
      const rings = ringConfigs.map((config) => {
        const ring = new THREE.Mesh(
          new THREE.TorusGeometry(config.radius, 0.018, 8, 160),
          new THREE.MeshBasicMaterial({
            color: config.color,
            transparent: true,
            opacity: 0.55,
          })
        );
        ring.rotation.set(...config.rotation);
        coreGroup.add(ring);
        return { mesh: ring, speed: config.speed };
      });

      const satellites = [];
      const satelliteConfig = [
        {
          position: [1.98, 0.48, 0.36],
          color: palette.core,
          size: 0.075,
          phase: 0,
        },
        {
          position: [-1.55, -0.94, 0.18],
          color: palette.warm,
          size: 0.09,
          phase: 1.4,
        },
        {
          position: [-0.74, 1.95, 0.22],
          color: palette.electric,
          size: 0.07,
          phase: 2.7,
        },
        {
          position: [0.9, -1.82, -0.16],
          color: palette.core,
          size: 0.065,
          phase: 4.1,
        },
        {
          position: [2.48, -0.38, -0.38],
          color: palette.electric,
          size: 0.055,
          phase: 5.3,
        },
      ];
      satelliteConfig.forEach((config) => {
        const node = new THREE.Mesh(
          new THREE.OctahedronGeometry(config.size, 0),
          new THREE.MeshBasicMaterial({ color: config.color })
        );
        node.position.set(...config.position);
        coreGroup.add(node);
        satellites.push({
          node,
          baseY: config.position[1],
          phase: config.phase,
        });
      });

      const particleCount = 180;
      const particlePositions = new Float32Array(particleCount * 3);
      const particleColors = new Float32Array(particleCount * 3);
      const colorChoices = [
        new THREE.Color(palette.core),
        new THREE.Color(palette.electric),
        new THREE.Color(palette.warm),
        new THREE.Color(palette.particle),
      ];
      for (let index = 0; index < particleCount; index += 1) {
        const radius = 2.2 + Math.random() * 2.9;
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(2 * Math.random() - 1);
        particlePositions[index * 3] = radius * Math.sin(phi) * Math.cos(theta);
        particlePositions[index * 3 + 1] = radius * Math.cos(phi) * 0.72;
        particlePositions[index * 3 + 2] =
          radius * Math.sin(phi) * Math.sin(theta) - 1.1;
        const particleColor = colorChoices[index % colorChoices.length];
        particleColors[index * 3] = particleColor.r;
        particleColors[index * 3 + 1] = particleColor.g;
        particleColors[index * 3 + 2] = particleColor.b;
      }
      const particleGeometry = new THREE.BufferGeometry();
      particleGeometry.setAttribute(
        "position",
        new THREE.BufferAttribute(particlePositions, 3)
      );
      particleGeometry.setAttribute(
        "color",
        new THREE.BufferAttribute(particleColors, 3)
      );
      const particles = new THREE.Points(
        particleGeometry,
        new THREE.PointsMaterial({
          size: 0.035,
          vertexColors: true,
          transparent: true,
          opacity: 0.78,
          depthWrite: false,
        })
      );
      scene.add(particles);

      scene.add(new THREE.AmbientLight(0xffffff, isDark ? 0.55 : 0.82));
      const coreLight = new THREE.PointLight(
        palette.core,
        isDark ? 2.4 : 1.35,
        12
      );
      coreLight.position.set(0.5, 0.8, 2.8);
      scene.add(coreLight);
      const edgeLight = new THREE.PointLight(
        palette.warm,
        isDark ? 1.45 : 0.8,
        9
      );
      edgeLight.position.set(-2.8, -1.4, 1.4);
      scene.add(edgeLight);

      this.sceneState = {
        scene,
        camera,
        renderer,
        coreGroup,
        core,
        outerCore,
        haloCore,
        innerWire,
        rings,
        satellites,
        particles,
        elapsed: 0,
      };

      this.onPointerMove = (event) => {
        const bounds = mount.getBoundingClientRect();
        this.pointer.x =
          ((event.clientX - bounds.left) / bounds.width - 0.5) * 2;
        this.pointer.y =
          ((event.clientY - bounds.top) / bounds.height - 0.5) * 2;
      };
      mount.addEventListener("pointermove", this.onPointerMove, {
        passive: true,
      });

      this.onResize = () => {
        if (!this.sceneState || !this.mountRef.current) return;
        const nextWidth = Math.max(this.mountRef.current.clientWidth, 1);
        const nextHeight = Math.max(this.mountRef.current.clientHeight, 1);
        this.sceneState.camera.aspect = nextWidth / nextHeight;
        this.sceneState.camera.updateProjectionMatrix();
        this.sceneState.renderer.setSize(nextWidth, nextHeight);
        this.sceneState.renderer.setPixelRatio(
          Math.min(window.devicePixelRatio || 1, 1.5)
        );
      };
      if (window.ResizeObserver) {
        this.resizeObserver = new window.ResizeObserver(this.onResize);
        this.resizeObserver.observe(mount);
      } else {
        window.addEventListener("resize", this.onResize);
      }

      this.visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          this.isVisible = entry.isIntersecting;
          if (this.isVisible) this.startAnimation();
        },
        { threshold: 0.05 }
      );
      this.visibilityObserver.observe(mount);

      this.onVisibilityChange = () => {
        if (!document.hidden && this.isVisible) this.startAnimation();
      };
      document.addEventListener("visibilitychange", this.onVisibilityChange);
      this.startAnimation();
    } catch (error) {
      // The CSS fallback remains visible if WebGL is unavailable.
      this.destroyScene();
    }
  };

  startAnimation = () => {
    if (this.frameId || !this.sceneState || document.hidden || !this.isVisible)
      return;
    this.frameId = window.requestAnimationFrame(this.renderFrame);
  };

  renderFrame = () => {
    this.frameId = null;
    const state = this.sceneState;
    if (!state || document.hidden || !this.isVisible) return;

    state.elapsed += this.reducedMotion ? 0.002 : 0.012;
    const t = state.elapsed;
    const motionScale = this.reducedMotion ? 0.22 : 1;

    state.coreGroup.rotation.y +=
      (this.pointer.x * 0.28 - state.coreGroup.rotation.y) * 0.035;
    state.coreGroup.rotation.x +=
      (-this.pointer.y * 0.2 - state.coreGroup.rotation.x) * 0.035;
    state.coreGroup.rotation.z = Math.sin(t * 0.35) * 0.09;

    state.core.rotation.y += 0.011 * motionScale;
    state.core.rotation.x += 0.006 * motionScale;
    state.outerCore.rotation.y -= 0.0024 * motionScale;
    state.haloCore.rotation.x += 0.003 * motionScale;
    state.innerWire.rotation.z -= 0.006 * motionScale;
    state.rings.forEach((ring) => {
      ring.mesh.rotation.z += ring.speed * motionScale;
    });
    state.satellites.forEach((satellite, index) => {
      satellite.node.rotation.x += 0.022 * motionScale;
      satellite.node.rotation.y += 0.015 * motionScale;
      satellite.node.position.y =
        satellite.baseY + Math.sin(t * 1.5 + satellite.phase) * 0.12;
      satellite.node.scale.setScalar(0.9 + Math.sin(t * 2.2 + index) * 0.12);
    });
    state.particles.rotation.y -= 0.0007 * motionScale;
    state.particles.rotation.x = Math.sin(t * 0.2) * 0.08;
    state.camera.position.x +=
      (this.pointer.x * 0.26 - state.camera.position.x) * 0.03;
    state.camera.position.y +=
      (-this.pointer.y * 0.22 + 0.08 - state.camera.position.y) * 0.03;
    state.camera.lookAt(state.scene.position);

    state.renderer.render(state.scene, state.camera);
    this.frameId = window.requestAnimationFrame(this.renderFrame);
  };

  destroyScene = () => {
    if (this.frameId) {
      window.cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
    if (this.onPointerMove && this.mountRef.current) {
      this.mountRef.current.removeEventListener(
        "pointermove",
        this.onPointerMove
      );
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    window.removeEventListener("resize", this.onResize);
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }
    document.removeEventListener("visibilitychange", this.onVisibilityChange);

    const state = this.sceneState;
    if (state) {
      state.scene.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) {
          const materials = Array.isArray(object.material)
            ? object.material
            : [object.material];
          materials.forEach((material) => material.dispose());
        }
      });
      state.renderer.dispose();
      if (state.renderer.forceContextLoss) state.renderer.forceContextLoss();
      if (
        this.mountRef.current &&
        state.renderer.domElement.parentNode === this.mountRef.current
      ) {
        this.mountRef.current.removeChild(state.renderer.domElement);
      }
    }
    this.sceneState = null;
  };

  render() {
    return (
      <div className="agent-core" ref={this.mountRef} aria-hidden="true">
        <div className="agent-core-fallback">
          <span className="fallback-core"></span>
          <span className="fallback-orbit fallback-orbit-one"></span>
          <span className="fallback-orbit fallback-orbit-two"></span>
          <span className="fallback-orbit fallback-orbit-three"></span>
        </div>
        <span className="agent-core-scanline"></span>
      </div>
    );
  }
}

export default AgentCore;
